import { Dotsmenu } from '@e3dc-react/icons'
import { IconButton, Menu, MenuItem, Button } from '@mui/material'
import React, { useState } from 'react'
import useModals from 'src/Hooks/useModals'
import { useDispatch } from 'react-redux'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { getInstallationConfigurationUrl, setActiveInstallationAndStorage } from 'src/Libs/installations/installation.functions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Installation } from 'src/Libs/installations/models'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { getInstallationDashboardUrl } from 'src/Libs/dashboard/routes'
import { useUserRoles } from 'src/Hooks/user'
import { ApiPermissionType } from 'src/Libs/api/types'

interface MenuItemsProps {
  installation: Installation
  showFullActionsMenu?: boolean
}

/**
 * This component is about tree dots menu which showing two options To Installation and Rename
 * To Installation navigate to the installation by id
 * Rename open a modal where we are able to set/rename installation name
 */

const InstallationLinkMenuItems: React.FC<MenuItemsProps> = props => {
  const { installation, showFullActionsMenu = true } = props
  const { openRenameInstallationModal } = useModals()
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const installationAccessType = useInstallationAccessType(installation)
  const { isInstaller } = useUserRoles()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)

  const dashboardEnabled = installationAccessType !== 'access-denied'
  const canRenameInstallation = !isInstaller || installation.permissionType === ApiPermissionType.rw

  const setInstallationAndNavigate = (link: string): void => {
    setActiveInstallationAndStorage(installation, dispatch)
    navigate(link)
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = (): void => {
    setAnchorEl(null)
  }

  const handleNavigateToDashboard = (): void => {
    handleCloseMenu()
    setInstallationAndNavigate(getInstallationDashboardUrl(installation))
  }

  const renameClickHandler = (): void => {
    if (!canRenameInstallation) return
    handleCloseMenu()
    openRenameInstallationModal({ installationId: installation.id, name: installation.partnersInstallationName })
  }

  if (!showFullActionsMenu) {
    if (installationAccessType === 'e3dc-setup-configurator-access') {
      return (
        <Button
          component={RouterLink}
          to={getInstallationConfigurationUrl(installation)}
          onClick={() => setActiveInstallationAndStorage(installation, dispatch)}
          variant="text"
          sx={{ marginLeft: 'auto' }}
        >
          {t('installation.menuOptions.toConfiguration')}
        </Button>
      )
    }

    return (
      <Button
        component={RouterLink}
        to={getInstallationDashboardUrl(installation)}
        onClick={() => setActiveInstallationAndStorage(installation, dispatch)}
        variant="text"
        disabled={!dashboardEnabled}
        sx={{ marginLeft: 'auto' }}
        data-qa={`button-installation-to-dashboard-${installation.id}`}
      >
        {t('installation.menuOptions.toInstallation')}
      </Button>
    )
  }

  return (
    <>
      <IconButton onClick={handleOpenMenu}>
        <Dotsmenu />
      </IconButton>
      <Menu open={isOpen} anchorEl={anchorEl} onClose={handleCloseMenu}>
        <MenuItem onClick={handleNavigateToDashboard} disabled={!dashboardEnabled} sx={{ marginLeft: 'auto' }}>
          {t('installation.menuOptions.toInstallation')}
        </MenuItem>
        <MenuItem onClick={renameClickHandler} disabled={!canRenameInstallation}>
          {t('installation.menuOptions.rename')}
        </MenuItem>
      </Menu>
    </>
  )
}

export default InstallationLinkMenuItems
